/**
 * Jurisdictions — Multi-state notification determination UI.
 *
 * Lets the compliance officer map affected individuals to their state of
 * residence, then renders a computed obligation matrix:
 *   - per-state individual notification deadline
 *   - per-state AG notification deadline (when triggered)
 *   - media notification flags (HIPAA ≥500 federal + state media thresholds)
 *   - earliest deadline across ALL jurisdictions (drives the incident clock)
 *
 * Why this exists: HIPAA's 60-day clock is a floor. CA SB 446 (eff Jan 1, 2026)
 * requires AG notice in 15 days; NY SHIELD, WA MHMDA, and others are tighter.
 * An MA plan with California members can miss a statutory deadline using a
 * single federal clock.
 */

import React, { useState, useEffect, useMemo } from 'react';

const API = process.env.REACT_APP_API_URL || '';

// ---------------------------------------------------------------------------
// Date helpers
// ---------------------------------------------------------------------------
function daysFromToday(isoDate) {
  if (!isoDate) return null;
  const d = new Date(isoDate);
  const t = new Date();
  t.setHours(0, 0, 0, 0);
  d.setHours(0, 0, 0, 0);
  return Math.ceil((d - t) / (24 * 60 * 60 * 1000));
}

function formatDate(isoDate) {
  if (!isoDate) return '—';
  try {
    return new Date(isoDate).toLocaleDateString('en-US', {
      month: 'short', day: 'numeric', year: 'numeric'
    });
  } catch {
    return isoDate;
  }
}

function urgencyClasses(days) {
  if (days == null) return 'text-slate-500 bg-slate-50 border-slate-200';
  if (days < 0) return 'text-red-700 bg-red-50 border-red-200';
  if (days <= 7) return 'text-red-700 bg-red-50 border-red-200';
  if (days <= 14) return 'text-amber-700 bg-amber-50 border-amber-200';
  if (days <= 30) return 'text-yellow-700 bg-yellow-50 border-yellow-200';
  return 'text-emerald-700 bg-emerald-50 border-emerald-200';
}

function urgencyLabel(days) {
  if (days == null) return '—';
  if (days < 0) return `${Math.abs(days)}d overdue`;
  if (days === 0) return 'Due today';
  return `${days}d remaining`;
}

// ---------------------------------------------------------------------------
// Main component
// ---------------------------------------------------------------------------
export default function JurisdictionsSection({ incident, incidentId, canEdit, onRefresh }) {
  const [stateLaws, setStateLaws] = useState([]);
  const [affectedStates, setAffectedStates] = useState(incident.affected_states || []);
  const [obligations, setObligations] = useState(incident.jurisdictional_obligations || null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [selectedState, setSelectedState] = useState('');
  const [residentsInput, setResidentsInput] = useState('');
  const [notesInput, setNotesInput] = useState('');
  const [submitting, setSubmitting] = useState(false);

  // Load the global state-law reference on mount
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await fetch(`${API}/api/privacy/state-laws`, { credentials: 'include' });
        const data = await res.json();
        if (!cancelled && data.success) setStateLaws(data.state_laws || []);
      } catch (_) {}
    })();
    return () => { cancelled = true; };
  }, []);

  // Reload affected-states + obligations from the server (fresh data after any change)
  const reload = async () => {
    setLoading(true);
    setError('');
    try {
      const res = await fetch(`${API}/api/privacy/incidents/${incidentId}/affected-states`, {
        credentials: 'include'
      });
      const data = await res.json();
      if (data.success) {
        setAffectedStates(data.affected_states || []);
        setObligations(data.jurisdictional_obligations || null);
      } else {
        setError(data.message || 'Failed to load');
      }
    } catch (e) {
      setError(e.message || 'Failed to load');
    } finally {
      setLoading(false);
    }
  };

  // Initial obligations may come from the parent — but also refresh on mount
  // so we get the freshest numbers.
  useEffect(() => { reload(); /* eslint-disable-next-line */ }, [incidentId]);

  const handleAdd = async (e) => {
    e.preventDefault();
    if (!selectedState || !residentsInput) return;
    setSubmitting(true);
    setError('');
    try {
      const res = await fetch(`${API}/api/privacy/incidents/${incidentId}/affected-states`, {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          state_code: selectedState,
          residents_affected: parseInt(residentsInput, 10) || 0,
          notes: notesInput || null
        })
      });
      const data = await res.json();
      if (data.success) {
        setSelectedState('');
        setResidentsInput('');
        setNotesInput('');
        await reload();
        onRefresh?.();
      } else {
        setError(data.message || 'Save failed');
      }
    } catch (err) {
      setError(err.message || 'Save failed');
    } finally {
      setSubmitting(false);
    }
  };

  const handleRemove = async (stateCode) => {
    if (!canEdit) return;
    setError('');
    try {
      const res = await fetch(
        `${API}/api/privacy/incidents/${incidentId}/affected-states/${stateCode}`,
        { method: 'DELETE', credentials: 'include' }
      );
      const data = await res.json();
      if (data.success) {
        await reload();
        onRefresh?.();
      } else {
        setError(data.message || 'Delete failed');
      }
    } catch (err) {
      setError(err.message || 'Delete failed');
    }
  };

  const availableStates = useMemo(() => {
    const taken = new Set((affectedStates || []).map((a) => a.state_code));
    return stateLaws.filter((l) => !taken.has(l.state_code));
  }, [stateLaws, affectedStates]);

  // ── Summary cards ───────────────────────────────────────────────────────
  const summary = obligations || {};
  const earliestDays = daysFromToday(summary.earliest_deadline_date);
  const federalDays = daysFromToday(summary.federal_deadline_date);

  return (
    <div className="space-y-6">
      {/* Hero strip — earliest deadline across all jurisdictions */}
      <div className="relative overflow-hidden rounded-3xl bg-gradient-to-br from-slate-900 via-slate-800 to-indigo-900 text-white shadow-xl">
        <div className="absolute inset-0 opacity-10" style={{
          backgroundImage: 'radial-gradient(circle at 20% 50%, white 1px, transparent 1px)',
          backgroundSize: '24px 24px'
        }} />
        <div className="relative p-7 flex flex-col lg:flex-row lg:items-center gap-6">
          <div className="flex-1">
            <p className="text-xs uppercase tracking-[0.18em] font-semibold text-indigo-200 mb-2">
              Earliest notification deadline
            </p>
            <div className="flex items-baseline gap-3 flex-wrap">
              <h2 className="text-4xl font-bold tracking-tight">
                {formatDate(summary.earliest_deadline_date)}
              </h2>
              {earliestDays != null && (
                <span className={`inline-flex px-3 py-1 rounded-full text-xs font-bold ${
                  earliestDays < 0 ? 'bg-red-500 text-white' :
                  earliestDays <= 14 ? 'bg-amber-400 text-slate-900' :
                  'bg-emerald-400 text-slate-900'
                }`}>
                  {urgencyLabel(earliestDays)}
                </span>
              )}
            </div>
            <p className="text-sm text-indigo-200 mt-2">
              Source: {summary.earliest_deadline_source || 'HIPAA 60-day federal floor'}
            </p>
          </div>
          <div className="grid grid-cols-3 gap-3 lg:gap-4 lg:min-w-[420px]">
            <StatPill label="Total affected" value={(summary.total_affected_residents ?? 0).toLocaleString()} />
            <StatPill label="Jurisdictions" value={summary.states_count ?? 0} />
            <StatPill label="AG notices" value={summary.ag_notifications_required ?? 0} />
          </div>
        </div>
      </div>

      {/* Federal floor reference card */}
      <div className="flex items-center gap-3 text-xs bg-blue-50 border border-blue-200 rounded-2xl px-4 py-3 text-blue-800">
        <svg className="w-4 h-4 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
        </svg>
        <span>
          <strong>HIPAA federal floor (60 days)</strong> · {formatDate(summary.federal_deadline_date)}
          {federalDays != null && ` · ${urgencyLabel(federalDays)}`}
          {' — '}
          State deadlines may be tighter. Earliest applicable clock drives the overall incident deadline.
        </span>
      </div>

      {/* Add-state form */}
      {canEdit && (
        <form onSubmit={handleAdd} className="bg-white rounded-2xl border border-slate-200 shadow-sm p-5">
          <h3 className="font-semibold text-slate-900 mb-3">Add affected jurisdiction</h3>
          <div className="grid grid-cols-1 md:grid-cols-12 gap-3">
            <div className="md:col-span-4">
              <label className="block text-xs font-medium uppercase tracking-wider text-slate-500 mb-1">State</label>
              <select
                value={selectedState}
                onChange={(e) => setSelectedState(e.target.value)}
                className="w-full rounded-xl border border-slate-200 px-3 py-2.5 text-slate-900 bg-white shadow-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                required
              >
                <option value="">Select a state...</option>
                {availableStates.map((l) => (
                  <option key={l.state_code} value={l.state_code}>{l.state_name}</option>
                ))}
              </select>
            </div>
            <div className="md:col-span-3">
              <label className="block text-xs font-medium uppercase tracking-wider text-slate-500 mb-1">Residents affected</label>
              <input
                type="number"
                min="0"
                value={residentsInput}
                onChange={(e) => setResidentsInput(e.target.value)}
                placeholder="e.g. 347"
                className="w-full rounded-xl border border-slate-200 px-3 py-2.5 text-slate-900 bg-white shadow-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                required
              />
            </div>
            <div className="md:col-span-3">
              <label className="block text-xs font-medium uppercase tracking-wider text-slate-500 mb-1">Notes (optional)</label>
              <input
                type="text"
                value={notesInput}
                onChange={(e) => setNotesInput(e.target.value)}
                placeholder="e.g. subset of member list"
                className="w-full rounded-xl border border-slate-200 px-3 py-2.5 text-slate-900 bg-white shadow-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              />
            </div>
            <div className="md:col-span-2 flex items-end">
              <button
                type="submit"
                disabled={submitting || !selectedState || !residentsInput}
                className="w-full px-4 py-2.5 rounded-xl bg-gradient-to-r from-blue-600 to-indigo-600 text-white text-sm font-semibold shadow-sm hover:shadow-lg hover:shadow-blue-600/30 disabled:opacity-50 disabled:shadow-none transition-all"
              >
                {submitting ? 'Saving...' : 'Add'}
              </button>
            </div>
          </div>
        </form>
      )}

      {error && (
        <div className="p-3 rounded-xl bg-red-50 border border-red-200 text-red-700 text-sm">
          {error}
        </div>
      )}

      {/* Obligations list */}
      {loading && affectedStates.length === 0 ? (
        <div className="text-center py-8 text-slate-400 text-sm">Loading jurisdictions...</div>
      ) : obligations?.obligations?.length > 0 ? (
        <div className="space-y-3">
          <h3 className="text-sm font-semibold text-slate-700 uppercase tracking-wider">
            Per-jurisdiction obligations
          </h3>
          {obligations.obligations.map((o) => (
            <ObligationCard
              key={o.state_code}
              obligation={o}
              canEdit={canEdit}
              onRemove={() => handleRemove(o.state_code)}
            />
          ))}
        </div>
      ) : (
        <div className="bg-white rounded-2xl border-2 border-dashed border-slate-200 p-10 text-center">
          <div className="w-14 h-14 rounded-2xl bg-slate-100 flex items-center justify-center mx-auto mb-4">
            <svg className="w-7 h-7 text-slate-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
            </svg>
          </div>
          <p className="text-slate-600 font-medium mb-1">No jurisdictions mapped yet</p>
          <p className="text-sm text-slate-400 max-w-md mx-auto">
            Add the states where affected individuals reside. The tool will compute per-jurisdiction deadlines for individual, AG, and media notice obligations.
          </p>
        </div>
      )}

      {/* Legal disclaimer */}
      <div className="text-xs text-slate-400 leading-relaxed border-t border-slate-200 pt-4">
        <strong>Disclaimer:</strong> State deadlines above are a best-effort reference maintained by the app author and verified as of each row&rsquo;s{' '}
        <code className="px-1 py-0.5 bg-slate-100 rounded text-slate-500">last_verified_on</code> date. Verify current statutory requirements with counsel before relying on these clocks for a specific incident. HIPAA&rsquo;s 60-day floor applies where state law is silent or longer.
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Subcomponents
// ---------------------------------------------------------------------------
function StatPill({ label, value }) {
  return (
    <div className="rounded-2xl bg-white/10 backdrop-blur-sm border border-white/15 px-4 py-3">
      <p className="text-[10px] uppercase tracking-wider text-indigo-200 font-semibold">{label}</p>
      <p className="text-2xl font-bold text-white mt-0.5">{value}</p>
    </div>
  );
}

function ObligationCard({ obligation, canEdit, onRemove }) {
  const o = obligation;
  const individualDays = daysFromToday(o.individual_deadline_date);
  const agDays = daysFromToday(o.ag_deadline_date);

  return (
    <div className="bg-white rounded-2xl border border-slate-200 shadow-sm hover:shadow-md transition-shadow overflow-hidden">
      <div className="p-5">
        <div className="flex items-start justify-between gap-4">
          <div className="flex items-start gap-4 flex-1 min-w-0">
            <StateBadge code={o.state_code} />
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2 flex-wrap">
                <h4 className="font-semibold text-slate-900">{o.state_name}</h4>
                <span className="text-xs text-slate-500">
                  · {o.residents_affected.toLocaleString()} resident{o.residents_affected === 1 ? '' : 's'}
                </span>
              </div>
              {o.citation && (
                <p className="text-xs text-slate-500 mt-0.5 truncate" title={o.citation}>
                  {o.citation}
                </p>
              )}
            </div>
          </div>
          {canEdit && (
            <button
              type="button"
              onClick={onRemove}
              className="flex-shrink-0 text-slate-400 hover:text-red-600 text-sm transition-colors px-2 py-1 rounded-lg hover:bg-red-50"
              aria-label="Remove state"
            >
              Remove
            </button>
          )}
        </div>

        {/* Obligation chips row */}
        <div className="mt-4 grid grid-cols-1 md:grid-cols-3 gap-2">
          {/* Individual notification */}
          <DeadlineChip
            label="Individual notice"
            date={o.individual_deadline_date}
            days={individualDays}
            subLabel={`${o.individual_deadline_days}d from discovery`}
          />
          {/* AG notification */}
          {o.ag_required ? (
            <DeadlineChip
              label="AG notification"
              date={o.ag_deadline_date}
              days={agDays}
              subLabel={o.ag_deadline_days ? `${o.ag_deadline_days}d from discovery` : 'without unreasonable delay'}
              accent="indigo"
            />
          ) : (
            <div className="rounded-xl border border-slate-100 bg-slate-50/50 px-3 py-2.5 text-xs text-slate-400">
              <p className="font-semibold uppercase tracking-wider text-[10px]">AG notification</p>
              <p className="mt-0.5">Not required for this count</p>
            </div>
          )}
          {/* Media notice */}
          {o.media_triggered ? (
            <div className="rounded-xl border border-amber-200 bg-amber-50 px-3 py-2.5">
              <p className="text-[10px] font-semibold uppercase tracking-wider text-amber-800">Media notice</p>
              <p className="text-xs text-amber-900 mt-0.5 font-medium">
                {o.federal_media_triggered && o.residents_affected >= 500 ? 'Required · HIPAA ≥500' : 'State threshold met'}
              </p>
            </div>
          ) : (
            <div className="rounded-xl border border-slate-100 bg-slate-50/50 px-3 py-2.5 text-xs text-slate-400">
              <p className="font-semibold uppercase tracking-wider text-[10px]">Media notice</p>
              <p className="mt-0.5">Not triggered</p>
            </div>
          )}
        </div>

        {o.notes && (
          <p className="mt-3 text-xs text-slate-500 italic border-l-2 border-slate-200 pl-3">{o.notes}</p>
        )}
      </div>
    </div>
  );
}

function DeadlineChip({ label, date, days, subLabel, accent }) {
  const classes = urgencyClasses(days);
  return (
    <div className={`rounded-xl border px-3 py-2.5 ${classes}`}>
      <p className="text-[10px] font-semibold uppercase tracking-wider opacity-80">{label}</p>
      <div className="flex items-baseline gap-2 mt-0.5">
        <p className="text-sm font-bold">{formatDate(date)}</p>
      </div>
      <p className="text-[11px] opacity-75 mt-0.5">
        {days != null ? urgencyLabel(days) : '—'} · {subLabel}
      </p>
    </div>
  );
}

function StateBadge({ code }) {
  return (
    <div className="flex-shrink-0 w-12 h-12 rounded-xl bg-gradient-to-br from-blue-500 to-indigo-600 flex items-center justify-center text-white font-bold text-sm shadow-md shadow-blue-500/20">
      {code}
    </div>
  );
}
